import symbolService from '@/services/symbolService';

export default {
  namespaced: true,
  state: {
    symbols: [],
    customSymbols: [],
    symbolsById: {},
    loading: false,
    error: null,
    selectedCurrency: 'USD',
    exchangeRate: 38.0,
  },
  mutations: {
    setSymbols(state, symbols) {
      state.symbols = symbols;
      state.symbolsById = symbols.reduce((acc, symbol) => {
        acc[symbol.id] = symbol;
        return acc;
      }, {});
    },
    setSymbolsById(state, symbols) {
      symbols.forEach(symbol => {
        state.symbolsById[symbol.id] = symbol;
      });
    },
    setCustomSymbols(state, symbols) {
      state.customSymbols = symbols;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setError(state, error) {
      state.error = error;
    },
    setSelectedCurrency(state, currency) {
      state.selectedCurrency = currency;
    },
    setExchangeRate(state, rate) {
      state.exchangeRate = rate;
    }
  },
  getters: {
    getConvertedPrice: (state) => (price, symbolType) => {
      if (!price) return 0;
      
      const sourceCurrency = ['US', 'ETF', 'COMMODITY', 'CRYPTO'].includes(symbolType) ? 'USD' : 'TL';
      
      if (state.selectedCurrency === sourceCurrency) return price;
      
      if (sourceCurrency === 'TL' && state.selectedCurrency === 'USD') {
        return price / state.exchangeRate;
      }
      
      if (sourceCurrency === 'USD' && state.selectedCurrency === 'TL') {
        return price * state.exchangeRate;
      }
      
      return price;
    },
    getSymbolById: (state) => (id) => {
      return state.symbolsById[id];
    }
  },
  actions: {
    async getAllSymbols({ commit }) {
      try {
        commit('setLoading', true);
        const response = await symbolService.getAllSymbols();
        
        const symbols = response.data;
        
        commit('setSymbols', symbols);
        return symbols;
      } catch (error) {
        console.error('Error in getAllSymbols action:', error);
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
    async searchSymbols({ commit }, query) {
      try {
        const response = await symbolService.searchSymbols(query);
        void commit; // Acknowledge unused parameter
        return response.data;
      } catch (error) {
        console.error('Error in searchSymbols action:', error);
        throw error;
      }
    },
    async deleteCustomSymbol({ dispatch, rootState }, symbolId) {
      try {
        // Then delete the symbol itself
        await symbolService.deleteSymbol(symbolId);
        
        // Update the store state using user's email as userId
        const userEmail = rootState.auth.user?.email;
        await dispatch('fetchCustomSymbols', userEmail);
      } catch (error) {
        console.error('Error deleting custom symbol:', error);
        throw error;
      }
    },
    async fetchCustomSymbols({ commit, rootState }, userId) {
      try {
        commit('setLoading', true);
        // If userId is not provided, use the authenticated user's email
        const userEmail = userId || rootState.auth.user?.email;
        const response = await symbolService.getCustomSymbols(userEmail);
        commit('setCustomSymbols', response.data);
        return response.data;
      } catch (error) {
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
    async createCustomSymbol({ commit, dispatch, rootState }, symbol) {
      try {
        commit('setLoading', true);
        // Set userId to user's email
        const symbolData = {
          ...symbol,
          userId: rootState.auth.user?.email
        };
        const response = await symbolService.createSymbol(symbolData);
        await dispatch('fetchCustomSymbols', symbolData.userId);
        return response.data;
      } catch (error) {
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
    async updateCustomSymbol({ commit, dispatch, rootState }, symbol) {
      try {
        commit('setLoading', true);
        // Set userId to user's email
        const symbolData = {
          ...symbol,
          userId: rootState.auth.user?.email
        };
        const response = await symbolService.updateSymbol(symbolData.symbolId, symbolData);
        await dispatch('fetchCustomSymbols', symbolData.userId);
        return response.data;
      } catch (error) {
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
    setSelectedCurrency({ commit }, currency) {
      commit('setSelectedCurrency', currency);
    },
    async updateExchangeRate({ commit }) {
      try {
        const response = await symbolService.getExchangeRate();
        const rate = response.data;
        
        if (rate && rate > 0) {
          commit('setExchangeRate', rate);
        } else {
          console.warn('Invalid exchange rate received:', rate);
          commit('setExchangeRate', 46.0);
        }
      } catch (error) {
        console.error('Error updating exchange rate:', error);
        commit('setExchangeRate', 46.0);
      }
    },
    async fetchSymbolsByIds({ commit }, symbolIds) {
      try {
        commit('setLoading', true);
        const response = await symbolService.getSymbolsByIds(symbolIds);
        
        commit('setSymbolsById', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching symbols by IDs:', error);
        commit('setError', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    }
  }
};
